import { z } from 'zod';
import { TimelyProjectIdSchema } from '../models.timesheet_proposals';
import { EmbeddedToolLightSchema } from '../models.tools';
import { IdentityWriteSchema } from './integration.models.identity';
import {
  ResourceMetadataReadSchema,
  ResourceMetadataWriteSchema,
} from './integration.models.resource.metadata';

/**
 * Resource type hierarchy - order matters for composition, not runtime.
 * @warning KEEP IN SYNC WITH `RESOURCE_DIFF_SQL` in
 * apps/integration-cloud/src/queries/api/timely/diff/resourceDiffQuery.ts
 */
export const ResourceRootTypeEnums = ['organization'] as const;
export const NonSubscribableResourceTypeEnums = ['task', 'tag', 'pull_request'] as const;
export const ResourceChildTypeEnums = [
  'team',
  'client',
  'project',
  ...NonSubscribableResourceTypeEnums,
] as const;
export const ResourceTypeEnums = [...ResourceRootTypeEnums, ...ResourceChildTypeEnums] as const;

export const NonSubscribableResourceTypeSchema = z.enum(NonSubscribableResourceTypeEnums);
export type NonSubscribableResourceType = z.infer<typeof NonSubscribableResourceTypeSchema>;

/** All possible `resource.type`'s */
export const ResourceRootTypeSchema = z.enum(ResourceRootTypeEnums);
export type ResourceRootType = z.infer<typeof ResourceRootTypeSchema>;
export const ResourceChildTypeSchema = z.enum(ResourceChildTypeEnums);
export const ResourceTypeSchema = z.enum([...ResourceRootTypeEnums, ...ResourceChildTypeEnums]);
export type ResourceType = z.infer<typeof ResourceTypeSchema>;
const SubscriptionTypeEnums = ['auto', 'manual'] as const;
export const SubscriptionTypeSchema = z.enum(SubscriptionTypeEnums).nullable();

/**
 * Resource Base Schema.
 */
export const ResourceBaseSchema = z.object({
  /** The primary ID of this record according to external source/import origin */
  external_id: z.coerce.string().describe('External source ID'),
  /** Unique identifiers for relationship building  */
  uri: z.string().url().describe('Unique resource identifier (synthetic URI)'),
  /** Title/name of this resource */
  title: z.string().describe('Resource title/name'),
  /**
   * (Soft) reference to the lowest level resource this belongs to, e.g the Github org
   *
   * **IMPORTANT:** Use `null` only for root-level resources (organizations).
   * All child resources should reference their root organization.
   *
   * @example root_uri: `https://api.github.com/orgs/myorg` // for all resources under myorg
   */
  root_uri: z.string().url().nullable().describe('URI of root organization resource'),

  /**
   * (Soft) reference to the parent resource, e.g the Github repository
   *
   * **IMPORTANT:** Use `null` only for root-level resources (organizations).
   * Projects reference organizations, tasks reference projects, etc.
   *
   * @example parent_uri: `https://api.github.com/repos/myorg/myrepo` // for tasks in myrepo
   */
  parent_uri: z.string().url().nullable().describe('URI of parent resource'),
  /** Which tool does this resource originate from? */
  tool_id: z.string().describe('Tool ID this resource belongs to'),

  /**
   * Resource subscription type - determines how users interact with this resource
   *
   * **Values:**
   * - `'auto'`: Resource is subscribed by default, user can opt out
   *   - Use for organizations/workspaces (root-level resources)
   * - `'manual'`: User must explicitly opt in to each resource
   *   - Use for second-level  resources under organizations (e.g, teams, clients, or projects)
   * - `null`: No subscription possible
   *   - Use for tasks/cards (parent resource)
   *   - Use for metadata resources (tags, labels)
   *   - Use for read-only or system resources
   *
   * @example subscription_type: 'auto' // for organizations/root-level resource
   * @example subscription_type: 'manual' // for tasks
   * @example subscription_type: null // for tags/labels
   */
  subscription_type: SubscriptionTypeSchema.describe(
    'Subscription behavior: auto, manual, or null'
  ),

  /**
   * External URL to visit to view this resource (user-facing web URL)
   *
   * **IMPORTANT:** Use `null` if the resource has no web interface.
   * This should be a clickable link for users to view the resource.
   *
   * @example url_html: board.url // Trello board web URL
   * @example url_html: null // for API-only resources
   */
  url_html: z.string().url().nullable().describe('User-facing web URL'),

  /**
   * API URL of this resource (canonical API endpoint)
   *
   * **REQUIRED:** This should be the definitive API URL for this resource.
   * Used for API operations and relationship building.
   *
   * @example url_api: `https://api.github.com/repos/${org}/${repo}`
   */
  url_api: z.string().url().describe('Canonical API endpoint URL'),

  /**
   * When was this resource created (in the external system)?
   *
   * **IMPORTANT:** Use `null` if the external API doesn't provide creation timestamps.
   * Database will use `NOW()` as fallback. Using artificial "now" timestamps in
   * transformers will cause unnecessary re-sync to Timely on every change.
   *
   * @example toDateTimeISOrNull(apiData.created_at) // null if undefined
   */
  created_at: z
    .string()
    .datetime({ offset: true })
    .nullable()
    .describe('External creation timestamp'),

  /**
   * When was this resource last updated (in the external system)?
   *
   * **IMPORTANT:** Use `null` if the external API doesn't provide update timestamps.
   * Database will use `NOW()` as fallback. Using artificial "now" timestamps in
   * transformers will cause unnecessary re-sync to Timely on every change.
   *
   * @example toDateTimeISOrNull(apiData.updated_at) // null if undefined
   */
  updated_at: z
    .string()
    .datetime({ offset: true })
    .nullable()
    .describe('External last-updated timestamp'),

  /**
   * When did this resource "start"? (calendar event, project start/end, etc.)
   *
   * Use `null` if the resource has no start date concept.
   * @example toDateTimeISOrNull(apiData.start_date)
   */
  start_date: z.string().datetime({ offset: true }).nullable().describe('Resource start date'),

  /**
   * When did this resource "end"? (calendar event, project start/end, etc.)
   *
   * Use `null` if the resource has no end date concept.
   * @example toDateTimeISOrNull(apiData.due_date)
   */
  end_date: z.string().datetime({ offset: true }).nullable().describe('Resource end/due date'),

  /**
   * If set, this resource is completed (e.g, task is done)
   *
   * Use `null` for incomplete resources. Only set when actually completed.
   * @example toDateTimeISOrNull(apiData.completed_at)
   */
  completed_at: z
    .string()
    .datetime({ offset: true })
    .nullable()
    .describe('When resource was completed'),

  /**
   * If set, this resource will be archived (omitted from most APIs by default)
   *
   * Use `null` for active resources. Only set when actually archived.
   * @example toDateTimeISOrNull(apiData.archived_at)
   */
  archived_at: z
    .string()
    .datetime({ offset: true })
    .nullable()
    .describe('When resource was archived'),

  /**
   * Set `deleted_at` to schedule resource for deletion
   *
   * Use `null` for active resources. Only set when actually deleted.
   * @example toDateTimeISOrNull(apiData.deleted_at)
   */
  deleted_at: z
    .string()
    .datetime({ offset: true })
    .nullable()
    .describe('When resource was deleted'),
});
export type ResourceBase = z.infer<typeof ResourceBaseSchema>;

export const ResourceReadBaseSchema = ResourceBaseSchema.extend({
  id: z.string().uuid().describe('TIC resource ID'),
  /** Joined via RPC, the parent resource, e.g the Github repository */
  parent_id: z.string().uuid().nullable().describe('TIC ID of parent resource'),
  /** Joint via RPC, the lowest level resource this belongs to, e.g the Github org */
  root_id: z.string().uuid().nullable().describe('TIC ID of root organization'),
  /** Minimal required meta data (but usually overwritten for each `resource.type`) */
  metadata: ResourceMetadataReadSchema,
  /** Database always populates these fields for read operations */
  created_at: z.string().datetime({ offset: true }).describe('TIC record creation timestamp'),
  updated_at: z.string().datetime({ offset: true }).describe('TIC record last-updated timestamp'),
});
export type ResourceReadBase = z.infer<typeof ResourceReadBaseSchema>;

export const ResourceWriteBaseSchema = ResourceBaseSchema.omit({
  created_at: true,
  updated_at: true,
}).extend({
  /** Optional on write - database will populate if not provided */
  created_at: z.string().datetime({ offset: true }).optional().nullable(),
  updated_at: z.string().datetime({ offset: true }).optional().nullable(),
  /**
   * Opt-in SHA-256 hex hash of the resource's meaningful content (TT-4934).
   *
   * When supplied, the SQL upsert uses this hash as the primary change detector:
   * matching hash skips the `last_synced_at` bump (and prevents a Timely diff),
   * differing hash bumps. Absent means the existing `updated_at`-based comparison
   * is used (backward compatible).
   *
   * Compute with `computeContentHash()` from the framework utils. Polling-first
   * providers whose resource types lack a meaningful external `updated_at`
   * (Monday.com workspaces, groups, tags) should set this; providers whose
   * resources have a real external `updated_at` (Jira projects, GitHub repos,
   * Monday items) can leave it null.
   *
   * @see apps/integration-cloud/src/integrations/utils/compute-content-hash.ts
   * @see https://linear.app/timelyapp/issue/TT-4934/
   */
  content_hash: z
    .string()
    .nullable()
    .optional()
    .describe('Opt-in content hash for change detection (TT-4934)'),
  /**
   * Users explicitly associated with this resource (assignees, creators, members, etc.)
   *
   * **Database Behavior:** When combined with `_storage_instructions._clear_nonreferenced_identities = true`,
   * this array determines which identity permissions are preserved. Identities NOT in this array
   * will have their `resource_permissions` records deleted from the database.
   *
   * **CRITICAL Usage Patterns:**
   * - `null`: Preserves ALL existing associations, no database changes to permissions
   * - `[]` (empty array): With `_clear_nonreferenced_identities = true`, removes ALL identity permissions
   * - `[...identities]`: With `_clear_nonreferenced_identities = true`, keeps only these identities
   *
   * **Identity Stub Pattern:** Create minimal identities when full user data isn't available.
   * These will be enriched during the `importIdentities` operation.
   *
   * @example
   * // Preserve existing associations (most common for organizations):
   * associated_identities: null
   *
   * @example
   * // Update membership list (projects/boards with changing membership):
   * associated_identities: memberIds.map(id => ({
   *   uri: `https://api.provider.com/users/${id}`,
   *   external_user_id: id,
   *   external_name: null, // Will be filled by importIdentities
   *   external_username: null, // Will be filled by importIdentities
   *   external_email: null,
   *   external_timezone: null,
   *   verified: false,
   *   avatar_url: null,
   *   last_changed_at: null,
   * }))
   *
   * @example
   * // Remove all identity associations:
   * associated_identities: []
   * // + _storage_instructions: { _clear_nonreferenced_identities: true }
   */
  associated_identities: z
    .array(IdentityWriteSchema)
    .nullable()
    .describe('Users associated with this resource'),
  /** Minimal required meta data (but usually overwritten for each `resource.type`) */
  metadata: ResourceMetadataWriteSchema,
  /**
   * Special instructions to database storage functions
   *
   * Controls how the database handles resource storage, permissions, and subscriptions.
   * These instructions are processed by `fn_upsert_resources_and_associations`
   * and `fn_upsert_resource`.
   */
  _storage_instructions: z
    .object({
      /**
       * **Database Behavior:** When `true` AND `associated_identities` is an array (not null),
       * deletes `resource_permissions` records where `identity_uri` is NOT in the provided
       * `associated_identities` array. Preserves account associations and system records.
       *
       * **Use Cases:**
       * - `true`: For resources where membership changes (projects, boards)
       * - `false`: For resources where you want to preserve existing permissions (organizations, tasks)
       *
       * **CRITICAL:** Only works when `associated_identities` is an array. Using `null`
       * for `associated_identities` disables this cleanup regardless of this flag.
       *
       * @example _clear_nonreferenced_identities: true // projects, boards with changing membership
       * @example _clear_nonreferenced_identities: false // organizations, tasks
       */
      _clear_nonreferenced_identities: z.coerce
        .boolean()
        .optional()
        .default(false)
        .describe('Remove identity permissions not in associated_identities'),

      /**
       * Forces the Postgres storage functions to enable the subscription irrespective of
       * `subscription_type`. Specifically consumed by
       * `public.upsert_resources_and_associations` and
       * `public.upsert_identities_and_link_associations` when deciding whether to set
       * `resource_subscriptions.enabled` / `identity_permissions.sync_to_timely` to `TRUE`.
       *
       * **Use Cases:**
       * - Force subscription for specific resources regardless of subscription_type
       * - One-time subscription bootstrap during import
       *
       * @example _auto_subscribe: false // most common
       * @example _auto_subscribe: true // force subscription during import
       */
      _auto_subscribe: z.coerce
        .boolean()
        .optional()
        .default(false)
        .describe('Force subscription regardless of subscription_type'),

      /**
       * When `true` AND a resource write includes `deleted_at`, the database will
       * soft-delete descendants (via `resources.parent_id` ancestry) as part of the same
       * storage call. This is used for delete/close webhook semantics where the provider
       * cannot re-fetch the deleted resource to enumerate children.
       *
       * @remarks
       * Descendant deletion is intentionally handled in storage (not per-provider code)
       * so every provider can share the same, consistent cleanup behavior.
       *
       * This is currently opt-in. If it proves safe for the majority of providers (>90%),
       * we likely want to flip the default to \"on\" and introduce an explicit opt-out
       * for the few cases where descendant deletion is unsafe (e.g. canonical/shared
       * resources that can appear under multiple roots, or resources whose ancestry is
       * not solely expressed via `resources.parent_id`).
       */
      _cascade_soft_delete_descendants: z.coerce
        .boolean()
        .optional()
        .describe('Soft-delete descendant resources when deleted_at is set'),

      /**
       * Preserves existing non-null `deleted_at` when an incoming upsert writes
       * `NULL` for that field.
       *
       * **Default behavior:** When omitted, storage treats this as `false`.
       * Storage otherwise follows standard overwrite semantics, so `deleted_at: null`
       * clears deleted state unless a caller explicitly opts into stickiness.
       *
       * **Use Cases:**
       * - omitted / `false`: `deleted_at: null` clears deleted state naturally
       * - `true`: partial refreshes that send `deleted_at: null` but must still preserve tombstones
       */
      _preserve_deleted_state: z.coerce
        .boolean()
        .optional()
        .describe('Keep deleted_at sticky even when explicit null is written'),

      /**
       * Preserves existing non-null `archived_at` when an incoming upsert writes
       * `NULL` for that field.
       *
       * **Default behavior:** When omitted, storage treats this as `false`.
       * Storage otherwise follows standard overwrite semantics, so `archived_at: null`
       * clears archived state unless a caller explicitly opts into stickiness.
       *
       * **Use Cases:**
       * - omitted / `false`: `archived_at: null` clears archived state naturally
       * - `true`: rare partial refreshes that send `archived_at: null` but must keep archive state sticky
       */
      _preserve_archived_state: z.coerce
        .boolean()
        .optional()
        .describe('Keep archived_at sticky even when explicit null is written'),
    })
    .nullable()
    .describe('Database storage behavior instructions'),
});
export type ResourceWriteBase = z.infer<typeof ResourceWriteBaseSchema>;

/**
 * Root Resource Base Schema
 * Represents the top-level organizational entity, hence doesn't have a parent/root,
 * but for type simplify, just force those values to `null`.
 */
export const RootResourceWriteBaseSchema = ResourceWriteBaseSchema.omit({
  root_uri: true,
  parent_uri: true,
}).extend({
  root_uri: z.null(),
  parent_uri: z.null(),
});

export type RootResourceWriteBase = z.infer<typeof RootResourceWriteBaseSchema>;

/**
 * Organization Resource Schema
 * Represents the root/top-level organizational entity.
 * e.g, Github org, Timely workspace, Asana Account, JIRA Space, etc.
 */
export const OrganizationResourceReadSchema = ResourceReadBaseSchema.extend({
  type: z.literal(ResourceRootTypeSchema.Enum.organization),
});
export type OrganizationResourceRead = z.infer<typeof OrganizationResourceReadSchema>;

export const OrganizationResourceWriteSchema = RootResourceWriteBaseSchema.extend({
  type: z.literal(ResourceRootTypeSchema.Enum.organization),
});
export type OrganizationResourceWrite = z.infer<typeof OrganizationResourceWriteSchema>;

/**
 * Client Resource Schema
 */
export const ClientResourceReadSchema = ResourceReadBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.client),
});
export const ClientResourceWriteSchema = ResourceWriteBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.client),
});
export type ClientResourceWrite = z.infer<typeof ClientResourceWriteSchema>;

/**
 * Project Resource Schema
 */
export const ProjectResourceReadSchema = ResourceReadBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.project),
});
export const ProjectResourceWriteSchema = ResourceWriteBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.project),
});
export type ProjectResourceWrite = z.infer<typeof ProjectResourceWriteSchema>;

/**
 * Task Resource Schema
 */
export const TaskResourceReadSchema = ResourceReadBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.task),
});
export const TaskResourceWriteSchema = ResourceWriteBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.task),
});
export type TaskResourceWrite = z.infer<typeof TaskResourceWriteSchema>;

export const PresentableTaskResourceSchema = TaskResourceReadSchema.extend({
  tool: EmbeddedToolLightSchema,
  timely_project_id: TimelyProjectIdSchema.optional().openapi({
    description: 'Timely project identifier this task maps to, when available',
    example: 4223913,
  }),
});
export type PresentableTaskResource = z.infer<typeof PresentableTaskResourceSchema>;

/**
 * Task Resource Schema
 */
export const PullRequestResourceReadSchema = ResourceReadBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.pull_request),
});
export const PullRequestResourceWriteSchema = ResourceWriteBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.pull_request),
});
export type PullRequestResourceWrite = z.infer<typeof PullRequestResourceWriteSchema>;

/**
 * Tag aka Label Resource Schema
 */
const TagResourceMetadataSchema = ResourceMetadataWriteSchema.extend({
  color: z.string().nullable(),
  emoji: z.string().nullable(),
});

export const TagResourceReadSchema = ResourceReadBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.tag),
  metadata: TagResourceMetadataSchema,
});

export const TagResourceWriteSchema = ResourceWriteBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.tag),
  metadata: TagResourceMetadataSchema,
});

export type TagResourceWrite = z.infer<typeof TagResourceWriteSchema>;

/**
 * Team Resource Schema
 */
export const TeamResourceReadSchema = ResourceReadBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.team),
});
export const TeamResourceWriteSchema = ResourceWriteBaseSchema.extend({
  type: z.literal(ResourceTypeSchema.Enum.team),
});
export type TeamResourceWrite = z.infer<typeof TeamResourceWriteSchema>;

/**
 * Combined Resource Schemas
 */
export const AnyReadResourceSchema = z.union([
  OrganizationResourceReadSchema,
  ClientResourceReadSchema,
  ProjectResourceReadSchema,
  TaskResourceReadSchema,
  TagResourceReadSchema,
  TeamResourceReadSchema,
  PullRequestResourceReadSchema,
]);
export type AnyReadResource = z.infer<typeof AnyReadResourceSchema>;

/**
 * Presentable Resource Schemas - With embedded tool information
 */
export const OrganizationPresentableResourceSchema = OrganizationResourceReadSchema.extend({
  tool: EmbeddedToolLightSchema,
});
export type OrganizationPresentableResource = z.infer<typeof OrganizationPresentableResourceSchema>;

export const ClientPresentableResourceSchema = ClientResourceReadSchema.extend({
  tool: EmbeddedToolLightSchema,
});
export type ClientPresentableResource = z.infer<typeof ClientPresentableResourceSchema>;

export const ProjectPresentableResourceSchema = ProjectResourceReadSchema.extend({
  tool: EmbeddedToolLightSchema,
});
export type ProjectPresentableResource = z.infer<typeof ProjectPresentableResourceSchema>;

export const PresentableTagResourceSchema = TagResourceReadSchema.extend({
  tool: EmbeddedToolLightSchema,
});
export type TagPresentableResource = z.infer<typeof PresentableTagResourceSchema>;

export const PullRequestPresentableResourceSchema = PullRequestResourceReadSchema.extend({
  tool: EmbeddedToolLightSchema,
});
export type PullRequestPresentableResource = z.infer<typeof PullRequestPresentableResourceSchema>;

export const PresentableTeamResourceSchema = TeamResourceReadSchema.extend({
  tool: EmbeddedToolLightSchema,
});
export type PresentableTeamResource = z.infer<typeof PresentableTeamResourceSchema>;

/**
 * Combined Presentable Resource Schema
 */
export const AnyPresentableResourceSchema = z.union([
  OrganizationPresentableResourceSchema,
  ClientPresentableResourceSchema,
  ProjectPresentableResourceSchema,
  PresentableTaskResourceSchema,
  PresentableTagResourceSchema,
  PresentableTeamResourceSchema,
  PullRequestPresentableResourceSchema,
]);

export type AnyPresentableResource = z.infer<typeof AnyPresentableResourceSchema>;

export const AnyWriteResourceSchema = z.union([
  OrganizationResourceWriteSchema,
  ClientResourceWriteSchema,
  ProjectResourceWriteSchema,
  TaskResourceWriteSchema,
  TagResourceWriteSchema,
  TeamResourceWriteSchema,
  PullRequestResourceWriteSchema,
]);
export type AnyWriteResource = z.infer<typeof AnyWriteResourceSchema>;

/**
 * Resource Schema Map
 * Each `resource.type` will be Zod parsed by the corresponding schema before storage
 */
export const ResourceWriteSchemaMap = {
  organization: OrganizationResourceWriteSchema,
  client: ClientResourceWriteSchema,
  project: ProjectResourceWriteSchema,
  task: TaskResourceWriteSchema,
  tag: TagResourceWriteSchema,
  team: TeamResourceWriteSchema,
  pull_request: PullRequestResourceWriteSchema,
} as const;
